import React, { use, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import ErrorMessage from '@components-clean/core/error';
import createGridCustomCells, { INamedItem } from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { DateTimeCell, YesNoCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import Loading from '@components-clean/core/loading/loading';
import DeleteFunctionalDataReviewDialog from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/delete-functional-data-review-dialog';
import UpdateFunctionalDataReviewDialog from '@pages-clean/risk-assessment/critical-to-quality-factors/dialogs/update-functional-data-review-dialog';
import { CTQFactorsContext } from '@pages-clean/risk-assessment/interfaces/context';
import { PersistentStorageKeys } from '@utils-clean/persistent-storage-keys';
import { AuditApplicationArea, AuditDataType, AuditServiceType, NamedItemType } from 'types/graphql-global-types';
import { Permissions } from '@contexts/user-context';
import { useAuthorization, useBoolean, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { stringDateWithoutTimeFromCurrentUtcDate } from '@utils/convertor';
import { GET_NAMED_ITEMS_BY_TYPE, GET_NAMED_ITEMS_BY_TYPE_FOR_STUDY } from '@queries/common';
import { getNamedItemsByType, getNamedItemsByTypeForStudy } from '@queries/generated/common.generated';
import { FunctionalDataReviewsIncludingCtQfDetailFields } from '@queries/generated/fragments.generated';
import { GET_FUNCTIONAL_DATA_REVIEWS_WITH_CTQF_DETAIL } from '@queries/risk-assessment';

const FunctionalDataReviews = () => {
    const user = useAuthorization();
    const canView = user?.canRead?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.Read,
    });
    const canEdit = user?.canWrite?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.ReadWrite,
    });

    if (!canView) return null;

    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    /* eslint-disable @eslint-react/rules-of-hooks */
    const { t } = useTranslation('ram', { keyPrefix: 'ctqf.functionalDataReviews' });
    const { current } = use(CTQFactorsContext);
    const { study } = current;
    const showUpdateFunctionalDataReviewsDialog = useBoolean(false);
    const showDeleteFunctionalDataReviewDialog = useBoolean(false);

    const exportFileName = t('grid.exportFileName');
    const { TooltipCell, ActiveInactiveCell } = createGridCustomCells();
    const [flattenedFunctionalDataReviews, setFlattenedFunctionalDataReviews] = useState([]);
    const [fdr, setFdr] = useState<FunctionalDataReviewsIncludingCtQfDetailFields>();

    const { loading, error, data } = useQuery(GET_FUNCTIONAL_DATA_REVIEWS_WITH_CTQF_DETAIL, {
        variables: { studyId: study?.id },
    });
    const { data: responsibleFunctionsData, loading: loadingResponsibleFunctions } =
        useQuery<getNamedItemsByTypeForStudy>(GET_NAMED_ITEMS_BY_TYPE_FOR_STUDY, {
            variables: {
                studyId: study?.id,
                type: NamedItemType.RESPONSIBLE_FUNCTION,
            },
        });
    const { data: edcEntryData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.CTQEDC_ENTRY },
    });
    const { data: responsibleVendorData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.VENDOR },
    });
    const { data: ctqTypeData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.CTQ_TYPE },
    });

    useEffect(() => {
        if (!data?.functionalDataReviewsIncludingCtQfDetail?.length) {
            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setFlattenedFunctionalDataReviews([]);
            return;
        }

        if (!loadingResponsibleFunctions && edcEntryData && responsibleVendorData && ctqTypeData) {
            const array = data.functionalDataReviewsIncludingCtQfDetail.map(
                (item: FunctionalDataReviewsIncludingCtQfDetailFields) => {
                    return {
                        ...item,
                        vendorList: item.vendors?.map((a) => a?.name).join(', '),
                        createdBy: item.createdBy ? item.createdBy.fullName : '',
                        updatedBy: item.updatedBy ? item.updatedBy.fullName : '',
                        ctQfCreatedBy: item.ctQfCreatedBy ? item.ctQfCreatedBy.fullName : '',
                        ctQfUpdatedBy: item.ctQfUpdatedBy ? item.ctQfUpdatedBy.fullName : '',
                    };
                },
            );

            let sortedResult = array.sort(
                (
                    a: FunctionalDataReviewsIncludingCtQfDetailFields,
                    b: FunctionalDataReviewsIncludingCtQfDetailFields,
                ) => {
                    if (a.description === null && b.description !== null) return 1;
                    if (b.description === null && a.description !== null) return -1;
                    if (a.description === null && b.description === null) return 0;
                    return a.description!.localeCompare(b.description!);
                },
            );

            sortedResult = sortedResult.sort(
                (
                    a: FunctionalDataReviewsIncludingCtQfDetailFields,
                    b: FunctionalDataReviewsIncludingCtQfDetailFields,
                ) => {
                    if (a.description && b.description && a.description === b.description) {
                        if (a.responsibleFunction === null && b.responsibleFunction !== null) return 1;
                        if (b.responsibleFunction === null && a.responsibleFunction !== null) return -1;
                        if (a.responsibleFunction === null && b.responsibleFunction === null) return 0;
                        if (
                            a.responsibleFunction &&
                            a.responsibleFunction.name &&
                            b.responsibleFunction &&
                            b.responsibleFunction.name
                        ) {
                            return a.responsibleFunction.name.localeCompare(b.responsibleFunction.name);
                        }
                    }

                    return 0;
                },
            );

            // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setFlattenedFunctionalDataReviews(sortedResult);
        }
    }, [data, loadingResponsibleFunctions, edcEntryData, responsibleVendorData, ctqTypeData]);
    /* eslint-enable @eslint-react/rules-of-hooks */

    const columns: GridColumnProps[] = [
        {
            id: 'functionalDataReview',
            field: 'aa',
            title: t('grid.columns.fdr.title'),
            enhancedGroupedColumnToggling: true,
            lockedByDefault: true,
            children: [
                {
                    id: 'description',
                    field: 'description',
                    title: t('grid.columns.fdr.columns.description'),
                    lockedByDefault: true,
                    minWidth: 180,
                    width: 1,
                    filter: 'text',
                    cells: {
                        data: TooltipCell({
                            isCsv: false,
                        }),
                    },
                },
                {
                    id: 'responsibleFunction',
                    field: 'responsibleFunction',
                    title: t('grid.columns.fdr.columns.responsibleFunction'),
                    lockedByDefault: true,
                    width: 1,
                    minWidth: 180,
                    filter: 'text',
                    filterKey: 'name',
                    lovData: responsibleFunctionsData?.namedItemsByTypeForStudy
                        ? responsibleFunctionsData.namedItemsByTypeForStudy
                              .map((item) => item?.name as string)
                              .sort((a, b) => {
                                  return a.localeCompare(b);
                              })
                        : [],
                    cells: {
                        data: ActiveInactiveCell({
                            inactive: (dataItem: INamedItem) => dataItem?.active === false,
                            renderValue: (dataItem: INamedItem) => dataItem?.name,
                        }),
                    },
                },
                {
                    id: 'comment',
                    field: 'comment',
                    title: t('grid.columns.fdr.columns.comment'),
                    lockedByDefault: true,
                    width: 1,
                    minWidth: 180,
                    filter: 'text',
                    cells: {
                        data: TooltipCell({
                            isCsv: false,
                        }),
                    },
                },
                {
                    id: 'createdBy',
                    field: 'createdBy',
                    title: t('grid.columns.fdr.columns.createdBy'),
                    width: 1,
                    minWidth: 150,
                    showByDefault: false,
                },
                {
                    id: 'createdDate',
                    field: 'createdDate',
                    title: t('grid.columns.fdr.columns.created'),
                    filter: 'date',
                    cells: { data: DateTimeCell('DD-MMM-yyyy') },
                    width: 1,
                    minWidth: 130,
                    cellName: 'DateCell',
                    showByDefault: false,
                },
                {
                    id: 'updatedBy',
                    field: 'updatedBy',
                    title: t('grid.columns.fdr.columns.updatedBy'),
                    width: 1,
                    minWidth: 150,
                    showByDefault: false,
                },
                {
                    id: 'updatedDate',
                    field: 'updatedDate',
                    title: t('grid.columns.fdr.columns.updated'),
                    filter: 'date',
                    cells: { data: DateTimeCell('DD-MMM-yyyy') },
                    width: 1,
                    minWidth: 130,
                    cellName: 'DateCell',
                    showByDefault: false,
                },
                {
                    width: 1,
                    minWidth: 200,
                    id: 'id',
                    field: 'recordId',
                    title: 'ID',
                    showByDefault: false,
                },
            ],
        },
        {
            id: 'criticalToQualityFactors',
            field: 'bb',
            title: t('grid.columns.ctqf.title'),
            enhancedGroupedColumnToggling: true,
            children: [
                {
                    id: 'risks',
                    field: 'noRisks',
                    title: t('grid.columns.ctqf.columns.risks'),
                    width: 1,
                    minWidth: 80,
                    filter: 'numeric',
                },
                {
                    id: 'ctQfDescription',
                    field: 'ctQfDescription',
                    title: t('grid.columns.ctqf.columns.description'),
                    width: 1,
                    minWidth: 300,
                    filter: 'text',
                    cells: {
                        data: TooltipCell({
                            isCsv: false,
                        }),
                    },
                },
                {
                    id: 'active',
                    field: 'active',
                    title: t('grid.columns.ctqf.columns.active'),
                    locked: false,
                    cells: { data: YesNoCell },
                    width: 1,
                    minWidth: 100,
                    filter: 'boolean',
                },
                {
                    id: 'type',
                    field: 'type',
                    title: t('grid.columns.ctqf.columns.type'),
                    locked: false,
                    width: 1,
                    minWidth: 120,
                    filter: 'text',
                    filterKey: 'name',
                    lovData: ctqTypeData?.namedItemsByType
                        ? ctqTypeData.namedItemsByType.map((item) => item?.name as string)
                        : [],
                    cells: {
                        data: ActiveInactiveCell({
                            inactive: (dataItem: INamedItem) => dataItem?.active === false,
                            renderValue: (dataItem: INamedItem) => dataItem?.name,
                        }),
                    },
                },
                {
                    id: 'justification',
                    field: 'justification',
                    title: t('grid.columns.ctqf.columns.justification'),
                    locked: false,
                    width: 1,
                    minWidth: 300,
                    filter: 'text',
                    cells: {
                        data: TooltipCell({
                            isCsv: false,
                        }),
                    },
                },
                {
                    id: 'system',
                    field: 'system',
                    title: t('grid.columns.ctqf.columns.sourceSystem'),
                    locked: false,
                    width: 1,
                    minWidth: 150,
                    filter: 'text',
                    cells: {
                        data: TooltipCell({
                            isCsv: false,
                        }),
                    },
                },
                {
                    id: 'datasource',
                    field: 'dataSource',
                    title: t('grid.columns.ctqf.columns.dataSource'),
                    locked: false,
                    width: 1,
                    minWidth: 100,
                    filter: 'text',
                    cells: {
                        data: TooltipCell({
                            isCsv: false,
                        }),
                    },
                },
                {
                    id: 'edcEntry',
                    field: 'edcEntry',
                    title: t('grid.columns.ctqf.columns.edcEntry'),
                    locked: false,
                    minWidth: 150,
                    width: 1,
                    filter: 'text',
                    filterKey: 'name',
                    lovData: edcEntryData?.namedItemsByType
                        ? edcEntryData.namedItemsByType.map((item) => item?.name as string)
                        : [],
                    cells: {
                        data: ActiveInactiveCell({
                            inactive: (dataItem: INamedItem) => dataItem?.active === false,
                            renderValue: (dataItem: INamedItem) => dataItem?.name,
                        }),
                    },
                },
                {
                    id: 'vendors',
                    field: 'vendorList',
                    title: t('grid.columns.ctqf.columns.vendors'),
                    width: 1,
                    minWidth: 300,
                    filter: 'text',
                    lovData: responsibleVendorData?.namedItemsByType
                        ? responsibleVendorData.namedItemsByType.map((item) => item?.name as string)
                        : [],
                    lovFilterContains: true,
                    cells: {
                        data: TooltipCell({
                            isCsv: true,
                            lovArrayField: 'vendors',
                        }),
                    },
                },
                {
                    id: 'isCritical',
                    field: 'critical',
                    title: t('grid.columns.ctqf.columns.isCritical'),
                    locked: false,
                    cells: { data: YesNoCell },
                    width: 1,
                    minWidth: 100,
                    filter: 'boolean',
                },
                {
                    id: 'ctQfCreatedBy',
                    field: 'ctQfCreatedBy',
                    title: t('grid.columns.ctqf.columns.createdBy'),
                    width: 1,
                    minWidth: 150,
                },
                {
                    id: 'ctQfCreatedDate',
                    field: 'ctQfCreatedDate',
                    title: t('grid.columns.ctqf.columns.created'),
                    filter: 'date',
                    cells: { data: DateTimeCell('DD-MMM-yyyy') },
                    width: 1,
                    minWidth: 130,
                    cellName: 'DateCell',
                },
                {
                    id: 'ctQfUpdatedBy',
                    field: 'ctQfUpdatedBy',
                    title: t('grid.columns.ctqf.columns.updatedBy'),
                    width: 1,
                    minWidth: 150,
                },
                {
                    id: 'ctQfUpdatedDate',
                    field: 'ctQfUpdatedDate',
                    title: t('grid.columns.ctqf.columns.updated'),
                    filter: 'date',
                    cells: { data: DateTimeCell('DD-MMM-yyyy') },
                    width: 1,
                    minWidth: 130,
                    cellName: 'DateCell',
                },
                {
                    width: 1,
                    minWidth: 200,
                    id: 'ctQfRecordId',
                    field: 'ctQfRecordId',
                    title: 'ID',
                    showByDefault: false,
                },
            ],
        },
    ];

    if (error) return <ErrorMessage />;

    if (!data) return <Loading />;

    return (
        <div style={{ height: 'calc(100cqh - 31px)' }}>
            <Grid
                excludeHiddenRowMenuActions
                title={t('grid.title')}
                maxHeight="100%"
                testDataId="fdr-grid"
                data={flattenedFunctionalDataReviews}
                columns={columns}
                loading={loading}
                useLoadingOverlay
                scrollable="virtual"
                showAddIcon={false}
                dataItemKey="id"
                canEdit={canEdit}
                rowMenuActions={[
                    {
                        id: 1,
                        label: t('grid.actions.edit'),
                        onClick: (dataItem: unknown) => {
                            const functionalDataReview = dataItem as FunctionalDataReviewsIncludingCtQfDetailFields;
                            setFdr(functionalDataReview);
                            showUpdateFunctionalDataReviewsDialog.setTrue();
                        },
                        disabled: !canEdit,
                        icon: ['far', 'pen-to-square'],
                    },
                    {
                        id: 2,
                        label: t('grid.actions.delete'),
                        onClick: (dataItem: unknown) => {
                            const functionalDataReview = dataItem as FunctionalDataReviewsIncludingCtQfDetailFields;
                            setFdr(functionalDataReview);
                            showDeleteFunctionalDataReviewDialog.setTrue();
                        },
                        icon: ['fas', 'trash-xmark'],
                        colorProp: 'Red',
                        disabled: !canEdit,
                    },
                ]}
                exportFilename={`${exportFileName}_${study?.studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate(
                    'DD-MMM-YYYY',
                )}`}
                service={AuditServiceType.CORE}
                area={AuditApplicationArea.RISK_ASSESSMENT}
                dataType={AuditDataType.EXCEL_EXPORT}
                persistentStorageKey={String(PersistentStorageKeys['functionDataReviews-grid'])}
            />
            {showUpdateFunctionalDataReviewsDialog.value && fdr && (
                <UpdateFunctionalDataReviewDialog
                    onClose={() => {
                        showUpdateFunctionalDataReviewsDialog.setFalse();
                    }}
                    fdr={fdr}
                />
            )}
            {showDeleteFunctionalDataReviewDialog.value && fdr && (
                <DeleteFunctionalDataReviewDialog
                    onClose={() => {
                        showDeleteFunctionalDataReviewDialog.setFalse();
                    }}
                    fdr={fdr}
                />
            )}
        </div>
    );
};

export default FunctionalDataReviews;
